import {
  CONTEXT_SCOPE_LABELS,
  CONTEXT_SCOPE_PRESETS,
  COLLECTION_KEYS,
  INHERITED_SELECTION,
  buildPrompt,
  collectionField,
  describeSource,
  describeSourceMapping,
  promptFilename,
  requirementGroupLabel,
  resolveContext,
  selectLayers,
  type CardDraft,
  type ContextLayer,
  type ContextScopeSelection,
  type RelatedComponent,
  type CardIntent,
  type PlanningInclusion,
  type PromptLocation,
} from '@blueprint/domain'
import { useEffect, useMemo, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button } from '../../components/controls.js'
import { useSessionStore } from '../../state/session-store.js'
import styles from './PromptPreviewDialog.module.css'

type CopyState = { kind: 'idle' } | { kind: 'copied' } | { kind: 'failed'; message: string }

interface PromptPreviewDialogProps {
  open: boolean
  onClose: () => void
  /** Values to preview: a committed card, or an editor's local draft. */
  draft: CardDraft
  location: PromptLocation | undefined
  cardId: string | null
  /** True when previewing an editor draft that differs from the saved card. */
  unsaved: boolean
  /**
   * Present only for the editor entry point. Inclusion changes then belong to the
   * editor's draft; without it they are temporary to this preview session.
   */
  onIntentChange?: ((next: CardIntent) => void) | undefined
  /** Scopes the card inherits architecture context from, least specific first. */
  contextParents?: readonly ContextLayer[] | undefined
  /** Typed relationships touching the card. */
  related?: readonly RelatedComponent[] | undefined
}

const PLANNING_FIELDS: ReadonlyArray<{ key: keyof PlanningInclusion; label: string }> = [
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'priority', label: 'Priority' },
  { key: 'moscow', label: 'MoSCoW' },
  { key: 'owner', label: 'Owner' },
  { key: 'tags', label: 'Tags' },
  { key: 'release', label: 'Release' },
  { key: 'dueDate', label: 'Due date' },
  { key: 'estimateMinutes', label: 'Estimate' },
  { key: 'colour', label: 'Card colour' },
  { key: 'location', label: 'Board location' },
]

/**
 * A read-only preview assembled from card values. Nothing is generated here, nothing is
 * saved, and opening it never changes the card, its status or the board.
 */
export function PromptPreviewDialog({
  open,
  onClose,
  draft,
  location,
  cardId,
  unsaved,
  onIntentChange,
  contextParents = [],
  related = [],
}: PromptPreviewDialogProps) {
  // Local working copy of the inclusion choices. Propagated to the editor draft when
  // this preview was opened from the editor; otherwise temporary to this session.
  const [intent, setIntent] = useState<CardIntent>(draft.intent)
  const [copy, setCopy] = useState<CopyState>({ kind: 'idle' })
  // Session-local, like the inclusion choices when previewing from the board.
  const [selection, setSelection] = useState<ContextScopeSelection>(INHERITED_SELECTION)
  const [provenance, setProvenance] = useState(true)
  // During a Teamcentric Accounts trial no data leaves the tenant: no copy or download.
  const trial = useSessionStore((state) => state.trial)

  useEffect(() => {
    if (!open) return
    setIntent(draft.intent)
    setCopy({ kind: 'idle' })
    setSelection(INHERITED_SELECTION)
    setProvenance(true)
    // Re-seed when the preview opens, not on every source keystroke.
  }, [open, cardId])

  // Source edits flow through: keep the working copy in step with everything except
  // the inclusion flags the user is toggling here.
  useEffect(() => {
    if (!open) return
    setIntent((current) => mergeInclusions(draft.intent, current))
  }, [open, draft.intent])

  const effectiveContext = useMemo(
    () =>
      resolveContext(selectLayers(contextParents, selection), {
        source: { scope: 'card', id: cardId ?? '', title: draft.title },
        context: draft.context,
      }),
    [contextParents, selection, cardId, draft.title, draft.context],
  )

  const result = useMemo(
    () => buildPrompt({ ...draft, intent, location, effectiveContext, related, provenance }),
    [draft, intent, location, effectiveContext, related, provenance],
  )
  const presetKey =
    CONTEXT_SCOPE_PRESETS.find((preset) =>
      contextParents.every((layer) => preset.selection[layer.source.scope] === selection[layer.source.scope]),
    )?.key ?? 'custom'
  const excludedItems = [
    ...COLLECTION_KEYS.flatMap((key) =>
      effectiveContext.excluded[key].map((item) => ({ ...item, label: collectionField(key).label, key })),
    ),
    ...effectiveContext.excludedSourceMappings.map((item) => ({
      ...item,
      value: describeSourceMapping(item.value),
      label: 'Source mappings',
      key: 'sourceMappings',
    })),
  ]

  const update = (next: CardIntent) => {
    setIntent(next)
    onIntentChange?.(next)
  }

  const setPlanning = (key: keyof PlanningInclusion, value: boolean) =>
    update({ ...intent, planning: { ...intent.planning, [key]: value } })

  const onCopy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard access is not available here.')
      await navigator.clipboard.writeText(result.markdown)
      setCopy({ kind: 'copied' })
    } catch (error) {
      // Clipboard writes are blocked on insecure origins; say so rather than claim success.
      setCopy({
        kind: 'failed',
        message:
          error instanceof Error && error.message.includes('not available')
            ? 'Clipboard access is not available on this address. Select the text below, or download the Markdown.'
            : 'The copy was refused by the browser. Select the text below, or download the Markdown.',
      })
    }
  }

  const onDownload = () => {
    const blob = new Blob([result.markdown], { type: 'text/markdown;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = promptFilename(cardId)
    anchor.click()
    URL.revokeObjectURL(url)
  }

  const includedRequirements = intent.requirements.filter(
    (entry) => entry.heading.trim().length > 0 || entry.body.trim().length > 0,
  )
  const usableReferences = intent.references.filter((reference) =>
    [reference.label, reference.locator, reference.relevance, reference.excerpt].some((v) => v.trim().length > 0),
  )

  return (
    <Dialog
      open={open}
      xwide
      title="Prompt preview"
      onClose={onClose}
      footer={
        !open ? null : (
        <>
          <span className={styles.footerNote}>
            {result.characterCount.toLocaleString()} characters. Character count, not tokens — a token count
            depends on the model you use.
          </span>
          {trial ? null : (
            <>
              <Button onClick={onDownload}>Download Markdown</Button>
              <Button variant="primary" data-autofocus onClick={() => void onCopy()}>
                Copy
              </Button>
            </>
          )}
          <Button onClick={onClose}>Close</Button>
        </>
        )
      }
    >
      {!open ? null : (
        <>
      <div className={`${styles.banner}`}>
        <strong>Built from your card details.</strong>
        <span>No AI generation happens in Blueprint, and nothing here is saved.</span>
        {unsaved ? <strong>Includes unsaved changes.</strong> : null}
      </div>

      {copy.kind === 'copied' ? (
        <div className={`${styles.banner} ${styles.ok}`} role="status">
          Copied to the clipboard.
        </div>
      ) : null}
      {copy.kind === 'failed' ? (
        <div className={`${styles.banner} ${styles.danger}`} role="alert">
          {copy.message}
        </div>
      ) : null}

      {result.overLimit ? (
        <div className={`${styles.banner} ${styles.danger}`} role="alert">
          <strong>
            This is {result.characterCount.toLocaleString()} characters, over the {result.exportLimit.toLocaleString()}{' '}
            limit.
          </strong>
          <span>
            Nothing has been shortened. The largest sections are{' '}
            {result.largestSections.map((section) => section.heading || 'the title').join(', ')}. Exclude or shorten
            material to bring it down.
          </span>
        </div>
      ) : null}

      {result.advisories.length > 0 ? (
        <div className={`${styles.banner} ${styles.warn}`}>
          <ul className={styles.advisories}>
            {result.advisories.map((advisory) => (
              <li key={advisory}>{advisory}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className={styles.layout}>
        <div>
          {/* Read-only and selectable: edit the card to change what appears here. */}
          <pre className={styles.output} tabIndex={0} aria-label="Assembled prompt" data-testid="prompt-output">
            {result.markdown}
          </pre>
        </div>

        <div className={styles.side}>
          {contextParents.length > 0 ? (
            <div className={styles.sideGroup}>
              <p className={styles.sideTitle}>Architecture context</p>
              <label className={styles.check}>
                <span className={styles.checkLabel}>Scopes</span>
                <select
                  aria-label="Context scopes"
                  value={presetKey}
                  onChange={(event) => {
                    const preset = CONTEXT_SCOPE_PRESETS.find((item) => item.key === event.target.value)
                    if (preset) setSelection(preset.selection)
                  }}
                >
                  {CONTEXT_SCOPE_PRESETS.map((preset) => (
                    <option key={preset.key} value={preset.key}>
                      {preset.label}
                    </option>
                  ))}
                  {presetKey === 'custom' ? <option value="custom">Custom</option> : null}
                </select>
              </label>
              {contextParents.map((layer) => (
                <label key={layer.source.scope} className={styles.check}>
                  <input
                    type="checkbox"
                    checked={selection[layer.source.scope]}
                    onChange={(event) => setSelection({ ...selection, [layer.source.scope]: event.target.checked })}
                  />
                  <span className={styles.checkLabel}>
                    {CONTEXT_SCOPE_LABELS[layer.source.scope]}: {layer.source.title}
                  </span>
                </label>
              ))}
              <label className={styles.check}>
                <input type="checkbox" checked={provenance} onChange={(event) => setProvenance(event.target.checked)} />
                <span className={styles.checkLabel}>Show where inherited values come from</span>
              </label>
              {excludedItems.length > 0 ? (
                <>
                  <p className={styles.muted}>Excluded from this prompt:</p>
                  <ul className={styles.advisories}>
                    {excludedItems.map((item) => (
                      <li key={`${item.key}-${item.value}`}>
                        {item.label}: {item.value} (from {describeSource(item.source)}, excluded by{' '}
                        {describeSource(item.excludedBy)})
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              {related.length > 0 ? (
                <p className={styles.muted}>
                  {related.length} related component{related.length === 1 ? '' : 's'} included.
                </p>
              ) : null}
            </div>
          ) : null}

          <div className={styles.sideGroup}>
            <p className={styles.sideTitle}>Card planning context</p>
            {PLANNING_FIELDS.map((field) => (
              <label key={field.key} className={styles.check}>
                <input
                  type="checkbox"
                  checked={intent.planning[field.key]}
                  onChange={(event) => setPlanning(field.key, event.target.checked)}
                />
                <span className={styles.checkLabel}>{field.label}</span>
              </label>
            ))}
            <p className={styles.muted}>Empty values are left out. Title and description are always included.</p>
          </div>

          {includedRequirements.length > 0 ? (
            <div className={styles.sideGroup}>
              <p className={styles.sideTitle}>Context entries</p>
              {includedRequirements.map((entry) => (
                <label key={entry.id} className={styles.check}>
                  <input
                    type="checkbox"
                    checked={entry.include}
                    onChange={(event) =>
                      update({
                        ...intent,
                        requirements: intent.requirements.map((item) =>
                          item.id === entry.id ? { ...item, include: event.target.checked } : item,
                        ),
                      })
                    }
                  />
                  <span className={styles.checkLabel}>
                    {entry.heading.trim() || requirementGroupLabel(entry.group)}
                  </span>
                </label>
              ))}
            </div>
          ) : null}

          {usableReferences.length > 0 ? (
            <div className={styles.sideGroup}>
              <p className={styles.sideTitle}>References</p>
              {usableReferences.map((reference) => (
                <label key={reference.id} className={styles.check}>
                  <input
                    type="checkbox"
                    checked={reference.include}
                    onChange={(event) =>
                      update({
                        ...intent,
                        references: intent.references.map((item) =>
                          item.id === reference.id ? { ...item, include: event.target.checked } : item,
                        ),
                      })
                    }
                  />
                  <span className={styles.checkLabel}>
                    {reference.label.trim() || 'Untitled reference'}
                    {reference.excerpt.trim().length > 0 ? ' (excerpt)' : ' (reference only)'}
                  </span>
                </label>
              ))}
            </div>
          ) : null}

          <p className={styles.muted}>
            {onIntentChange
              ? 'These choices are part of your unsaved card edits and are kept when you save.'
              : 'These choices apply to this preview only and are not saved to the card.'}
          </p>
        </div>
      </div>
        </>
      )}
    </Dialog>
  )
}

/** Take content from the source, but keep the inclusion flags the user set here. */
function mergeInclusions(source: CardIntent, working: CardIntent): CardIntent {
  const requirementFlags = new Map(working.requirements.map((entry) => [entry.id, entry.include]))
  const referenceFlags = new Map(working.references.map((reference) => [reference.id, reference.include]))
  return {
    ...source,
    planning: working.planning,
    requirements: source.requirements.map((entry) => ({
      ...entry,
      include: requirementFlags.get(entry.id) ?? entry.include,
    })),
    references: source.references.map((reference) => ({
      ...reference,
      include: referenceFlags.get(reference.id) ?? reference.include,
    })),
  }
}
